/**
 * Jul-2026 (User-Wunsch) — Einheitlicher Termin-Kalender (Datum + Uhrzeit),
 * der in JEDEM Browser identisch aussieht (Monatsraster links, Stunden- und
 * Minuten-Spalten rechts) — Ersatz für das native datetime-local-Feld.
 * Wert-Format wie datetime-local: "YYYY-MM-DDTHH:mm".
 */
import { useMemo, useRef, useState, useEffect } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli",
    "August", "September", "Oktober", "November", "Dezember"];
const pad = (n) => String(n).padStart(2, "0");

function parseValue(value) {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || "");
    if (!m) return null;
    return { y: +m[1], mo: +m[2], d: +m[3], h: +m[4], mi: +m[5] };
}

function monthGrid(y, mo) {
    const first = new Date(y, mo - 1, 1);
    const startOffset = (first.getDay() + 6) % 7; // 0 = Montag
    const cells = [];
    for (let i = 0; i < 42; i++) {
        const dt = new Date(y, mo - 1, 1 - startOffset + i);
        cells.push({ y: dt.getFullYear(), mo: dt.getMonth() + 1, d: dt.getDate(), outside: dt.getMonth() + 1 !== mo });
    }
    return cells;
}

export default function DateTimeField({ value, onChange, className = "", "data-testid": testId }) {
    const [open, setOpen] = useState(false);
    const sel = parseValue(value);
    const now = new Date();
    const [view, setView] = useState({ y: sel?.y || now.getFullYear(), mo: sel?.mo || now.getMonth() + 1 });
    useEffect(() => {
        if (open) {
            const s = parseValue(value);
            setView({ y: s?.y || now.getFullYear(), mo: s?.mo || now.getMonth() + 1 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const cells = useMemo(() => monthGrid(view.y, view.mo), [view]);
    const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const selDateStr = sel ? `${sel.y}-${pad(sel.mo)}-${pad(sel.d)}` : "";

    const emit = (p) => onChange(`${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`);
    const pickDate = (c) => {
        emit({ ...c, h: sel?.h ?? 10, mi: sel?.mi ?? 0 });
        if (c.outside) setView({ y: c.y, mo: c.mo });
    };
    const base = () => sel || { y: view.y, mo: view.mo, d: now.getDate(), h: 10, mi: 0 };
    const pickHour = (h) => emit({ ...base(), h });
    // Aug-2026 (User-Wunsch): nach Wahl der Minute (letzter Schritt) schließt
    // das Feld automatisch, damit es die anderen Eingaben nicht verdeckt.
    const pickMin = (mi) => { emit({ ...base(), mi }); setOpen(false); };
    const pickToday = () => {
        const mi5 = Math.round(now.getMinutes() / 5) * 5;
        emit({ y: now.getFullYear(), mo: now.getMonth() + 1, d: now.getDate(),
            h: mi5 === 60 ? Math.min(now.getHours() + 1, 23) : now.getHours(), mi: mi5 % 60 });
        setView({ y: now.getFullYear(), mo: now.getMonth() + 1 });
    };
    const prevMonth = () => setView((v) => (v.mo === 1 ? { y: v.y - 1, mo: 12 } : { y: v.y, mo: v.mo - 1 }));
    const nextMonth = () => setView((v) => (v.mo === 12 ? { y: v.y + 1, mo: 1 } : { y: v.y, mo: v.mo + 1 }));

    const hourRef = useRef(null);
    const minRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => {
            hourRef.current?.querySelector('[data-active="1"]')?.scrollIntoView({ block: "center" });
            minRef.current?.querySelector('[data-active="1"]')?.scrollIntoView({ block: "center" });
        }, 30);
        return () => clearTimeout(t);
    }, [open, value]);

    const label = sel ? `${pad(sel.d)}.${pad(sel.mo)}.${sel.y}  ${pad(sel.h)}:${pad(sel.mi)}` : "";

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button type="button" data-testid={testId}
                    className={`flex h-9 w-full items-center justify-between rounded-sm border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-ring ${className}`}>
                    <span className={label ? "font-mono-num" : "text-muted-foreground"}>{label || "tt.mm.jjjj --:--"}</span>
                    <CalendarDays className="w-4 h-4 text-gray-500 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0 rounded-sm" data-testid={testId ? `${testId}-popup` : undefined}>
                <div className="flex">
                    <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-bold">{MONTHS[view.mo - 1]} {view.y}</div>
                            <div className="flex gap-1">
                                <button type="button" onClick={prevMonth} className="h-7 w-7 inline-flex items-center justify-center rounded-sm border border-gray-200 hover:bg-gray-100" data-testid={testId ? `${testId}-prev-month` : undefined}><ChevronLeft className="w-4 h-4" /></button>
                                <button type="button" onClick={nextMonth} className="h-7 w-7 inline-flex items-center justify-center rounded-sm border border-gray-200 hover:bg-gray-100" data-testid={testId ? `${testId}-next-month` : undefined}><ChevronRight className="w-4 h-4" /></button>
                            </div>
                        </div>
                        <div className="grid grid-cols-7 gap-y-0.5 text-center">
                            {WEEKDAYS.map((w) => <div key={w} className="w-8 h-7 text-[11px] font-bold text-gray-500 flex items-center justify-center">{w}</div>)}
                            {cells.map((c, i) => {
                                const ds = `${c.y}-${pad(c.mo)}-${pad(c.d)}`;
                                const isSel = ds === selDateStr;
                                const isToday = ds === todayStr;
                                return (
                                    <button key={i} type="button" onClick={() => pickDate(c)}
                                        data-testid={isSel && testId ? `${testId}-selected-day` : undefined}
                                        className={`w-8 h-8 rounded-sm text-sm flex items-center justify-center transition-colors
                                            ${isSel ? "bg-[#111827] text-white font-bold" : isToday ? "bg-orange-100 text-orange-800 font-bold" : c.outside ? "text-gray-300 hover:bg-gray-100" : "text-gray-800 hover:bg-gray-100"}`}>
                                        {c.d}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex justify-between mt-2 pt-2 border-t border-gray-100">
                            <button type="button" onClick={() => { onChange(""); }} className="text-xs font-bold text-gray-500 hover:text-red-600" data-testid={testId ? `${testId}-clear` : undefined}>Löschen</button>
                            <button type="button" onClick={pickToday} className="text-xs font-bold text-orange-600 hover:text-orange-700" data-testid={testId ? `${testId}-today` : undefined}>Heute</button>
                        </div>
                    </div>
                    <div className="flex border-l border-gray-200">
                        <div ref={hourRef} className="h-72 overflow-y-auto py-1 px-1 border-r border-gray-100" data-testid={testId ? `${testId}-hours` : undefined}>
                            <div className="text-[10px] font-bold text-gray-400 text-center pb-0.5 sticky top-0 bg-white">Std</div>
                            {Array.from({ length: 24 }, (_, h) => (
                                <button key={h} type="button" onClick={() => pickHour(h)} data-active={sel?.h === h ? "1" : "0"}
                                    className={`block w-11 py-1.5 rounded-sm text-sm text-center font-mono-num transition-colors ${sel?.h === h ? "bg-[#111827] text-white font-bold" : "hover:bg-gray-100 text-gray-700"}`}>
                                    {pad(h)}
                                </button>
                            ))}
                        </div>
                        <div ref={minRef} className="h-72 overflow-y-auto py-1 px-1" data-testid={testId ? `${testId}-minutes` : undefined}>
                            <div className="text-[10px] font-bold text-gray-400 text-center pb-0.5 sticky top-0 bg-white">Min</div>
                            {Array.from({ length: 12 }, (_, i) => i * 5).map((mi) => (
                                <button key={mi} type="button" onClick={() => pickMin(mi)} data-active={sel?.mi === mi ? "1" : "0"}
                                    className={`block w-11 py-1.5 rounded-sm text-sm text-center font-mono-num transition-colors ${sel?.mi === mi ? "bg-[#111827] text-white font-bold" : "hover:bg-gray-100 text-gray-700"}`}>
                                    {pad(mi)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
